import React, { useMemo, useState } from "react";
import {
  ChatShell,
  ChangeReviewPanel,
  Bubble,
  ReviewContinueAction,
  ReviewRequirementCue,
  ScenarioPanel,
} from "../shared/components";
import {
  EDIT_VALUE,
  FLAG_FOR_FINAL_REVIEW,
  NOT_REVIEWED,
  conditionDefinition,
} from "../shared/conditionContract";
import { SOURCE_EVIDENCE_VERSION, TIMELINE, SCENARIO, sourceEvidenceForField } from "../shared/mockData";
import { FinalReview } from "../shared/FinalReview";
import { FORM_SCHEMA, MOCK_FINAL_RECORD } from "../shared/formSchema";
import { orderReviewEditsBySchema } from "../shared/fieldOrder";
import { updateRecord } from "../shared/useStudySession";
import { useTimeline } from "../shared/useTimeline";
import { currentTurnReview } from "../shared/currentTurnReview";

export default function FactorialDemo({ condition }) {
  const contract = conditionDefinition(condition);
  const instant = contract.verification_schedule === "INSTANT";
  const required = contract.response_requirement === "REQUIRED";
  const { revealed, done, decide: advanceTimeline } = useTimeline(TIMELINE, {
    pauseOnEdit: instant && required,
  });
  const [drafts, setDrafts] = useState({});
  const [reviewStatuses, setReviewStatuses] = useState({});
  const [mismatchActions, setMismatchActions] = useState({});
  const [corrections, setCorrections] = useState({});
  const [finalReviewOpen, setFinalReviewOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [record, setRecord] = useState(MOCK_FINAL_RECORD);

  const normalizedItems = useMemo(
    () => revealed
      .map((item, index) => {
        if (item.kind === "msg") {
          return { ...item, id: item.id || `demo-message-${index}` };
        }
        const evidence = item.field_id ? sourceEvidenceForField(item.field_id) : [];
        return {
          ...item,
          id: String(index),
          event_id: `demo-proposal-${index}`,
          proposal_id: `demo-proposal-${index}`,
          proposal_version: 1,
          record_version: index + 1,
          ai_proposed_value: item.value,
          source_evidence_id: item.field_id ? `evidence.${item.field_id}.v1` : null,
          source_evidence_version: SOURCE_EVIDENCE_VERSION,
          source_fragment_ids: evidence.map((entry) => entry.sourceFragmentId),
        };
      }),
    [revealed]
  );
  const messages = useMemo(
    () => normalizedItems.filter((item) => item.kind === "msg"),
    [normalizedItems]
  );
  const edits = useMemo(
    () => normalizedItems.filter((item) => item.kind === "edit"),
    [normalizedItems]
  );
  const instantTurn = useMemo(
    () => currentTurnReview(normalizedItems),
    [normalizedItems]
  );
  const visibleReviewEdits = useMemo(
    () => orderReviewEditsBySchema(
      instant ? instantTurn.edits : edits,
      FORM_SCHEMA,
      record
    ),
    [edits, instant, instantTurn.edits, record]
  );
  const pending = visibleReviewEdits.filter(
    (edit) => (reviewStatuses[edit.proposal_id] || NOT_REVIEWED) === NOT_REVIEWED
  );
  const showReview = visibleReviewEdits.length > 0
    && !submitted
    && !finalReviewOpen
    && (instant || done);
  const showVerificationContinueFooter = done && showReview;

  const changeStatus = (edit, status) => {
    setReviewStatuses((current) => ({ ...current, [edit.proposal_id]: status }));
    if (instant && required) advanceTimeline(Number(edit.id), status, edit.value);
    return true;
  };

  const chooseMismatchAction = async (edit, action) => {
    if (action !== FLAG_FOR_FINAL_REVIEW) return false;
    setMismatchActions((current) => ({ ...current, [edit.proposal_id]: action }));
    return true;
  };

  const correct = async (edit, value) => {
    const eventId = `demo-correction:${edit.proposal_id}:${Object.keys(corrections).length}`;
    setCorrections((current) => ({
      ...current,
      [eventId]: {
        event_id: eventId,
        event_name: "field_edit_committed",
        proposal_id: edit.proposal_id,
        proposal_version: edit.proposal_version,
        field_id: edit.field_id,
        value,
      },
    }));
    setRecord((current) => updateRecord(current, edit.field_id, value));
    setMismatchActions((current) => ({ ...current, [edit.proposal_id]: EDIT_VALUE }));
    setDrafts((current) => {
      const next = { ...current };
      delete next[edit.id];
      return next;
    });
    return true;
  };

  const finalReviewAnnotations = useMemo(() => {
    const correctionByProposal = {};
    for (const correction of Object.values(corrections)) {
      correctionByProposal[correction.proposal_id] = correction;
    }
    const annotations = {};
    for (const edit of edits) {
      const status = reviewStatuses[edit.proposal_id] || NOT_REVIEWED;
      if (status === "DOES_NOT_MATCH_SOURCE") {
        if (correctionByProposal[edit.proposal_id]) {
          annotations[edit.field_id] = {
            tone: "edited",
            lines: contract.verification_card.mismatch_edited_copy,
          };
        } else if (mismatchActions[edit.proposal_id] === FLAG_FOR_FINAL_REVIEW) {
          annotations[edit.field_id] = {
            tone: "flagged",
            text: contract.verification_card.mismatch_flagged_copy,
          };
        }
      } else if (status === "CANNOT_DETERMINE") {
        annotations[edit.field_id] = {
          tone: "unclear",
          text: contract.verification_card.unclear_copy,
        };
      }
    }
    return annotations;
  }, [contract.verification_card, corrections, edits, mismatchActions, reviewStatuses]);

  const renderReviewPanel = () => (
    <ChangeReviewPanel
      schedule={contract.verification_schedule}
      responseRequirement={contract.response_requirement}
      verificationCard={contract.verification_card}
      edits={visibleReviewEdits}
      corrections={corrections}
      reviewStatuses={reviewStatuses}
      mismatchActions={mismatchActions}
      drafts={drafts}
      submitted={submitted}
      showContinue={done && !showVerificationContinueFooter}
      continueDisabled={required && pending.length > 0}
      onContinue={() => setFinalReviewOpen(true)}
      onDraftChange={(id, value) => setDrafts((current) => ({ ...current, [id]: value }))}
      onStatusChange={changeStatus}
      onMismatchAction={chooseMismatchAction}
      onCorrect={correct}
    />
  );

  return (
    <div className="study-root">
      <div className="study-topbar">
        <h1>Accident Report</h1>
        <span className="cond-badge">{condition}</span>
      </div>
      <div className="study-split scenario">
        <ScenarioPanel scenario={SCENARIO} />
        <ChatShell
          locked={instant && required && pending.length > 0 && !done}
          lockedReason={contract.verification_card.required_validation_copy}
          disabled={done || finalReviewOpen}
          scrollToBottomKey={`${messages[messages.length - 1]?.id || "empty"}:${done ? "done" : "streaming"}`}
          notice={showReview ? (
            <ReviewRequirementCue
              responseRequirement={contract.response_requirement}
              progressionCue={contract.progression_cue}
            />
          ) : null}
          footer={showVerificationContinueFooter ? (
            <ReviewContinueAction
              continueDisabled={required && pending.length > 0}
              requiredValidationCopy={contract.verification_card.required_validation_copy}
              onContinue={() => setFinalReviewOpen(true)}
            />
          ) : null}
        >
          {messages.map((item) => (
            <React.Fragment key={item.id}>
              {showReview && instant && instantTurn.beforeMessageId === item.id && renderReviewPanel()}
              <Bubble m={item} />
            </React.Fragment>
          ))}
          {showReview && (!instant || !instantTurn.beforeMessageId) && renderReviewPanel()}
          {finalReviewOpen && !submitted && (
            <FinalReview
              record={record}
              setRecord={setRecord}
              onSubmit={() => setSubmitted(true)}
              fieldAnnotations={finalReviewAnnotations}
            />
          )}
          {submitted && <div className="chat-sys ok">Report submitted ✓</div>}
        </ChatShell>
      </div>
    </div>
  );
}
